import { InfoCircleOutlined } from '@ant-design/icons';
import CalendarOutlined from '@ant-design/icons/CalendarOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import HistoryOutlined from '@ant-design/icons/lib/icons/HistoryOutlined';
import PlusCircleOutlined from '@ant-design/icons/PlusCircleOutlined';
import ProfileOutlined from '@ant-design/icons/ProfileOutlined';
import RobotOutlined from '@ant-design/icons/RobotOutlined';
import UploadOutlined from '@ant-design/icons/UploadOutlined';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import Layout from 'antd/lib/layout';
import Menu from 'antd/lib/menu';
import MenuItem from 'antd/lib/menu/MenuItem';
import { ColumnType, Key } from 'antd/lib/table/interface';
import Typography from 'antd/lib/typography';
import { without } from 'lodash';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SidebarGrid } from 'src/components/Sidebar/SidebarGrid';
import { CustomizableColumnType, CustomizableTable } from 'src/components/table/CustomizableTable';
import { CustomizableTableContextProvider } from 'src/components/table/CustomizableTableContext';
import { AbilityContext } from '../../components/ability/can';
import { ConnectSVG, MaintenanceSVG } from '../../components/icons/icons';
import { IntervalDataTag } from '../../components/IntervalDataTag/IntervalDataTag';
import { AuditTrailModal } from '../../components/modals/AuditTrailModal/AuditTrailModal';
import { PageFooter } from '../../components/PageFooter';
import { PageHeader } from '../../components/PageHeader';
import { ResourceTelemetryStatusCell } from '../../components/ResourceTelemetryStatusCell/ResourceTelemetryStatusCell';
import { SyncSalesforceButton } from '../../components/salesforce/SyncSalesforceButton/SyncSalesforceButton';
import { SearchInput } from '../../components/searchInput/searchInput';
import { useCompanies } from '../../components/selectors/GlobalCompanySelector/CompanyContext';
import { SiteRapsImportModal } from '../../components/site-rap/SiteRapsImportModal';
import { ViewRapInfoModal } from '../../components/site-rap/ViewRapInfoModal';
import { AttachProgramBatchModal } from '../../components/site/AttachProgramBatchModal';
import { BatchUnenrollProgramsModal } from '../../components/site/BatchUnenrollProgramsModal';
import { CloseSiteModal } from '../../components/site/CloseSiteModal';
import { DeleteSiteModal } from '../../components/site/DeleteSiteModal';
import { EnrollProgramModal } from '../../components/site/EnrollProgramModal';
import { ExtraSiteInformation } from '../../components/site/ExtraSiteInformation';
import { ImportSitesModal } from '../../components/site/ImportSitesModal/ImportSitesModal';
import { MergeSitesModal } from '../../components/site/merge-sites/MergeSitesModal';
import { useCheckIfAllowedToMergeSites } from '../../components/site/merge-sites/useCheckIfAllowedToMergeSites';
import { OpenSiteModal } from '../../components/site/OpenSiteModal';
import { PartnerIdStatus } from '../../components/site/PartnerIdStatus';
import { SiteAddress } from '../../components/site/SiteAddress';
import { SiteEnrollmentExportButton } from '../../components/site/SiteEnrollmentExportButton';
import { SiteFormModal } from '../../components/site/SiteFormModal';
import { SiteMarketAndLoadZone } from '../../components/site/SiteMarketAndLoadZone';
import { SiteName } from '../../components/site/SiteName';
import { SiteSANs } from '../../components/site/SiteSANs';
import { UpdateSitesBatchModal } from '../../components/site/UpdateSitesBatchModal';
import { SiteVensInfo } from '../../components/SiteVensInfo/SiteVensInfo';
import { ActionList } from '../../components/table/actionList/actionList';
import { getColumnSortOrder } from '../../components/table/columnFormatHelpers';
import { useTableSelection } from '../../components/table/useTableSelection';
import { useDocumentTitle } from '../../components/useDocumentTitle';
import { ViewSwitcher } from '../../components/viewSwitcher/ViewSwitcher';
import { WithControlledTooltip } from '../../components/withControlledTooltip';
import { JobName } from '../../domain/async-job/interface';
import { AuditEntityType } from '../../domain/audit-trail/interface';
import { useAuth } from '../../domain/auth/useAuth';
import { DEFAULT_PAGINATION, PAGE_ACTION } from '../../domain/commonConst';
import { compareWithLocale } from '../../domain/compareWithLocale';
import { IPaginationMeta } from '../../domain/IPagination';
import { parseBoolean } from '../../domain/parseBoolean';
import { buildProgramTableFilter } from '../../domain/program/buildProgramTableFilter';
import { useProgramListByCompanyQuery, useProgramListQuery } from '../../domain/program/queries';
import { buildSiteDispatchTypeTableFilter } from '../../domain/site/buildSiteDispatchTypeTableFilter';
import { buildSiteStatusTableFilter } from '../../domain/site/buildSiteStatusTableFilter';
import { buildSiteUtilityTableFilter } from '../../domain/site/buildSiteUtilityTableFilter';
import { getActiveSiteSan } from '../../domain/site/getActiveSiteSan';
import { hasActualIntervalData } from '../../domain/site/hasActualIntervalData';
import { IEnrollSiteToPrograms, ISite, SITE_ACTION, SPECIFIC_SITE_ACTION } from '../../domain/site/interface';
import { useSiteDeleteMutation, useSiteEnrollProgramsMutation, useSiteListQuery, useSiteUtilitiesListQuery } from '../../domain/site/queries';
import { UserType } from '../../domain/user/interface';
import { validatePagination } from '../../domain/validatePagination';
import { DISABLED_COLOR, PRIMARY_COLOR } from '../../theme';
import { toUsefulParams } from '../toUsefulParams';
import { usePageLocation } from '../usePageState';
import { CustomerVenFormModal } from '../ven/components/CustomerVenFormModal';
import { SitesSidebar } from './sidebar/SitesSidebar';
import { ISitesSidebarFilters } from './sidebar/SitesSidebarFiltersContext';
import { useSitesSidebar } from './sidebar/useSitesSidebar';
import { newSite, prepareVen } from './siteInterface';
import { siteViewInterface } from './view/siteViewInterface';
import './sites.css';

export function Sites() {
    const { notification } = App.useApp();
    const location = useLocation();
    const navigation = useNavigate();
    const { setPageQuery, queryToState } = usePageLocation();
    const pageState: any = queryToState(location.search);
    const auth = useAuth()!;
    const ability = useContext(AbilityContext);
    const { companies, companyId } = useCompanies()!;

    useDocumentTitle('Sites');

    const isClosed = parseBoolean(pageState.filter?.isClosed);

    const [site, setSite] = useState<ISite | Partial<ISite>>();
    const { selectedKeys: selectedSiteIds, selectedItems: selectedSites, handleSelectedItems } = useTableSelection('site_id');

    const isAllowedToMerge = useCheckIfAllowedToMergeSites(selectedSites);

    const [siteAction, setSiteAction] = useState<SITE_ACTION | ''>();

    const { data: programsData } = useProgramListQuery({});
    const programs = useMemo(() => programsData?.data || [], [programsData?.data]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const filterPrograms = useProgramListByCompanyQuery({ company_id: companyId })?.data || [];
    const programsByCompany = useMemo(() => filterPrograms, [companyId, filterPrograms.length]);
    const programsToDisplay = useMemo(() => (companyId ? programsByCompany : programs), [companyId, programsByCompany]);

    const { data: utilitiesData } = useSiteUtilitiesListQuery();
    const utilities = useMemo(() => utilitiesData || [], [utilitiesData]);

    const { mutateAsync: deleteSite, isLoading: isDeleteLoading } = useSiteDeleteMutation();
    const { mutateAsync: enrollSiteToPrograms, isLoading: isEnrollProgramLoading } = useSiteEnrollProgramsMutation();

    const isAdminRole = useMemo(() => auth.user?.isAdminRoleType(), [auth.user?.user_type]);
    const isControlProviderRole = useMemo(() => auth.user?.isControlProvider(), [auth.user?.user_type]);
    const isCompanyColumnHidden = useMemo(() => [UserType.CUSTOMER, UserType.OPERATOR].includes(auth?.user?.user_type!), [auth.user?.user_type]);

    const { isFiltersSidebarEnabled, sidebarCollapsed, setSidebarCollapsed, layoutPaddingLeft } = useSitesSidebar();
    const [columns, setColumns] = useState<CustomizableColumnType<ISite>[]>([]);

    const listQuery = {
        pagination: pageState?.pagination ?? DEFAULT_PAGINATION,
        filter: {
            ...pageState?.filter,
            isClosed,
        },
        ...(pageState?.sorter && { sorter: pageState?.sorter }),
        ...(pageState?.search && { search: pageState?.search }),
        ...(pageState?.companyId && { company_id: pageState?.companyId }),
        include: isAdminRole
            ? 'company,control_provider,utility_customer,program,lmp,meter,meter_enrollment,customer_ven,cloud_ven,company_customer_ven,resource_telemetry'
            : 'company,control_provider,utility_customer,program,lmp,customer_ven',
    };

    const {
        data,
        isLoading,
        isFetching,
        isError,
        error,
        refetch: refetchSiteList,
    } = useSiteListQuery(listQuery, {
        keepPreviousData: true,
    });
    const sites = data?.data || [];
    const meta =
        data?.meta ??
        ({
            total: 0,
            offset: 0,
            limit: pageState && pageState.pagination && pageState.pagination.pageSize ? pageState.pagination.pageSize : DEFAULT_PAGINATION.pageSize,
        } as IPaginationMeta);

    if (isError) {
        notification.error({ key: 'sites-fetch-error', message: error?.message || 'Cannot fetch sites!' });
    }

    function showNewSiteModal() {
        setSiteAction(PAGE_ACTION.NEW_EDIT);
        setSite(newSite);
    }

    const showSiteActionModal = (record: ISite, action: SITE_ACTION) => () => {
        setSiteAction(action);
        setSite({ ...record });
    };

    const toggleClosedSites = useCallback(async () => {
        setPageQuery({
            ...pageState,
            filter: { ...pageState.filter, isClosed: !isClosed },

            // Reset pagination to the first page
            pagination: {
                ...pageState.pagination,
                current: DEFAULT_PAGINATION.current,
            },
        });
    }, [pageState, isClosed, setPageQuery]);

    async function deleteSiteAction(site: ISite) {
        try {
            await deleteSite(site);
            setSite(undefined);

            // remove deleted sites from selected items
            if (selectedSiteIds && selectedSiteIds.includes(site.site_id)) {
                const updatedKeys = without(selectedSiteIds, site.site_id);
                handleSelectedItems(updatedKeys, selectedSites);
            }

            const validPagination = validatePagination(pageState.pagination, meta ? meta.total - 1 : 0);

            setPageQuery({ ...pageState, pagination: validPagination });
            await refetchSiteList({ ...pageState, pagination: validPagination });

            notification.info({ key: 'site-delete-info', message: 'Site deleted' });
        } catch (err: any) {
            notification.error({ key: 'site-delete-error', message: err.message || 'Cannot delete site!' });
        }
    }

    async function showImportModal() {
        setSiteAction(SPECIFIC_SITE_ACTION.IMPORT);
    }

    async function onImportModalClose(shouldRefetch: boolean = false) {
        setSiteAction('');

        if (shouldRefetch) {
            await refetchSiteList(listQuery);
        }
    }

    async function onTableChange(pagination: any, filter: any, sorter: any) {
        // Merge the current pageState.filter with the new table filter,
        // giving precedence to new filter values
        const newFilter = {
            ...pageState.filter,
            ...filter,
            isClosed: parseBoolean(pageState.filter?.isClosed),
        };

        // Preserve the existing column configuration
        const preservedColumns = columns.map(col => ({
            ...col,
            hidden: col.hidden,
            width: col.width,
        }));

        const preparedParams = toUsefulParams({
            pagination,
            filter: newFilter,
            sorter,
        });

        setPageQuery({
            ...pageState,
            ...preparedParams,
        });

        setColumns(preservedColumns);
    }

    async function onSidebarFilterChange(updatedFilters: ISitesSidebarFilters) {
        await refetchSiteList(listQuery);
    }

    async function onSiteFormModalClose(site?: Partial<ISite>, closeModal = true) {
        if (closeModal) {
            setSite(undefined);
            setSiteAction('');
        }

        if (site) {
            await refetchSiteList(listQuery);
        }
    }

    async function onEnrollProgramModalClose(siteId?: number, enrollments?: IEnrollSiteToPrograms) {
        if (siteId && enrollments) {
            try {
                await enrollSiteToPrograms({ siteId, enrollments });
                notification.info({ key: 'enroll-program-info', message: 'Saved' });

                await refetchSiteList(listQuery);
            } catch (error: any) {
                notification.error({ key: 'enroll-program-error', message: error.message || 'Cannot save data!' });
            }
        }

        setSite(undefined);
        setSiteAction('');
    }

    async function closeSiteBatchModal(isRefreshNeeded: boolean) {
        setSiteAction(undefined);

        if (isRefreshNeeded) {
            await refetchSiteList(listQuery);
        }
    }

    async function onMergeSitesModalClose(refetch?: boolean) {
        setSiteAction('');

        if (refetch) {
            await refetchSiteList(listQuery);
            handleSelectedItems([], sites);
        }
    }

    async function closeBatchUnenrollModal(isRefreshNeeded: boolean) {
        setSiteAction(undefined);

        if (isRefreshNeeded) {
            await refetchSiteList(listQuery);
        }
    }

    function closeModal() {
        setSiteAction(undefined);
    }

    const handleSearch = useCallback(
        (value: string) => {
            if (value === pageState.search) {
                setPageQuery({
                    ...pageState,
                    pagination: { ...pageState.pagination, current: DEFAULT_PAGINATION.current },
                });
                return;
            }

            setPageQuery({ ...pageState, search: value });
        },
        [pageState, setPageQuery]
    );

    const gotoEventsHistory = (site: ISite) => {
        const companyId = site.company_id;
        const siteId = site.site_id;
        navigation(`/events/history?companyId=${companyId}&siteId=${siteId}`);
    };

    const redirectToConnectWebsiteAction = (record: ISite) => () => {
        window.open(record.connect_url, '_blank');
    };

    const onViewChange = (view: string) => {
        const companyQuery = pageState.companyId ? `?companyId=${pageState.companyId}` : '';
        navigation(`${view}${companyQuery}`);
    };

    const actions = [
        ...(ability.can('update', 'Site')
            ? [
                  {
                      title: (site: ISite) => {
                          if (site.closed_at) {
                              return 'To edit the site, please open it first.';
                          }

                          return 'Edit';
                      },
                      onClick: (site: ISite) => {
                          if (site.closed_at) {
                              return null;
                          }

                          return showSiteActionModal(site, PAGE_ACTION.NEW_EDIT);
                      },
                      icon: (site: ISite) => {
                          return <EditOutlined style={{ color: site.closed_at ? DISABLED_COLOR : 'inherit' }} />;
                      },
                  },
              ]
            : []),
        ...(ability.can('delete', 'Site')
            ? [
                  {
                      title: 'Delete',
                      onClick: (record: ISite) => showSiteActionModal(record, PAGE_ACTION.DELETE),
                      dataAction: 'delete',
                  },
              ]
            : []),
        ...(ability.can('close-open', 'Site')
            ? [
                  {
                      title: (record: ISite) => (record.closed_at ? 'Open Site' : 'Close Site'),
                      onClick: (record: ISite) =>
                          showSiteActionModal(record, record.closed_at ? SPECIFIC_SITE_ACTION.OPEN_SITE : SPECIFIC_SITE_ACTION.CLOSE_SITE),
                      icon: <MaintenanceSVG />,
                  },
              ]
            : []),
        ...(ability.can('enroll', 'Program') || ability.can('enroll', 'PriceResponse') || ability.can('enroll', 'CleanResponse')
            ? [
                  {
                      title: (site: ISite) => {
                          if (site.closed_at) {
                              return 'To attach program to the site, please open it first.';
                          }

                          return 'Attach program';
                      },
                      onClick: (site: ISite) => {
                          if (site.closed_at) {
                              return null;
                          }

                          return showSiteActionModal(site, PAGE_ACTION.ENROLL_PROGRAM);
                      },
                      icon: (site: ISite) => {
                          return <ProfileOutlined style={{ color: site.closed_at ? DISABLED_COLOR : 'inherit' }} />;
                      },
                  },
              ]
            : []),
        ...(isAdminRole
            ? [
                  {
                      title: 'Audit Trail',
                      onClick: (record: ISite) => showSiteActionModal(record, PAGE_ACTION.AUDIT_TRAIL),
                      icon: <HistoryOutlined />,
                  },
              ]
            : []),
        ...(ability.can('create', 'CustomerVen')
            ? [
                  {
                      title: (site: ISite) => {
                          if (site.closed_at) {
                              return 'To update VEN, please open it first';
                          }

                          if (site.customer_ven) {
                              return 'Edit VEN';
                          }

                          return 'Add VEN';
                      },
                      onClick: (record: ISite) => {
                          if (record.closed_at) {
                              return null;
                          }

                          return showSiteActionModal(record, SPECIFIC_SITE_ACTION.ADD_VEN);
                      },
                      icon: (record: ISite) => {
                          let color = 'inherit';

                          if (record.customer_ven) {
                              color = PRIMARY_COLOR;
                          }

                          if (record.closed_at) {
                              color = DISABLED_COLOR;
                          }

                          return <RobotOutlined style={{ color }} />;
                      },
                  },
              ]
            : []),
        ...(ability.can('read', 'Event')
            ? [
                  {
                      title: (record: ISite) => 'Open Events History',
                      onClick: (record: ISite) => () => gotoEventsHistory(record),
                      icon: (record: ISite) => <CalendarOutlined />,
                  },
              ]
            : []),
        ...(ability.can('connect', 'Site')
            ? [
                  {
                      title: (record: ISite) => {
                          if (record.can_enroll_interval_data && !hasActualIntervalData(record)) {
                              return 'Connect Site';
                          }

                          if (hasActualIntervalData(record)) {
                              return 'Site is already connected';
                          }

                          return 'Connect Site option is unsupported for this site, please contact the account manager.';
                      },
                      onClick: (record: ISite) =>
                          record?.can_enroll_interval_data && record?.connect_url ? redirectToConnectWebsiteAction(record) : undefined,
                      icon: (record: ISite) => {
                          let iconColor = DISABLED_COLOR;

                          if (record.can_enroll_interval_data) {
                              iconColor = 'inherit';
                          }

                          if (hasActualIntervalData(record)) {
                              iconColor = PRIMARY_COLOR;
                          }

                          return <ConnectSVG style={{ color: iconColor }} />;
                      },
                  },
              ]
            : []),
        ...(isAdminRole
            ? [
                  {
                      title: 'View Site RAP',
                      onClick: (record: ISite) => showSiteActionModal(record, SPECIFIC_SITE_ACTION.VIEW_RAP),
                      icon: <InfoCircleOutlined />,
                  },
              ]
            : []),
    ];

    const siteColumns = [
        ...(isCompanyColumnHidden
            ? []
            : [
                  {
                      key: 'company_id',
                      title: 'Company',
                      dataIndex: ['company_id'],
                      width: 85,
                      render: (text: number, record: ISite) => {
                          return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                  <PartnerIdStatus partner_id_status={record.partner_id_status} />
                                  <Typography.Text>{record?.company?.company_name}</Typography.Text>
                              </div>
                          );
                      },
                  },
              ]),
        {
            key: 'site_name',
            title: 'Site Name',
            dataIndex: 'site_name',
            sorter: (a: ISite, b: ISite) => compareWithLocale(a.site_name, b.site_name),
            ...getColumnSortOrder('site_name', pageState.sorter),
            render: (text, record) => <SiteName site={record} />,

            hideable: false,
        },
        ...(ability.can('read', 'ControlProvider')
            ? [
                  {
                      key: 'control_provider_id',
                      title: 'Control Provider',
                      dataIndex: 'control_provider_id',
                      sorter: true,
                      ...getColumnSortOrder('control_provider_id', pageState.sorter),
                      render: (text: string, record: ISite) => <Typography.Text>{record.control_provider?.company_name}</Typography.Text>,
                  },
              ]
            : []),
        ...(ability.can('read', 'UtilityCustomer')
            ? [
                  {
                      key: 'utility_customer_id',
                      title: 'Utility Customer',
                      dataIndex: 'utility_customer_id',
                      sorter: true,
                      ...getColumnSortOrder('utility_customer_id', pageState.sorter),
                      render: (text: string, record: ISite) => <Typography.Text>{record.utility_customer?.company_name}</Typography.Text>,
                  } as ColumnType<ISite>,
              ]
            : []),
        {
            key: 'site_store_number',
            title: 'Store #',
            width: 90,
            dataIndex: 'site_store_number',
            sorter: (a: ISite, b: ISite) => compareWithLocale(a.site_store_number, b.site_store_number),
            ...getColumnSortOrder('site_store_number', pageState.sorter),
        },
        {
            key: 'address',
            title: 'Address',
            render: (text, record) => <SiteAddress address={record.site_address} city={record.site_city} state={record.site_state} />,
        },
        {
            key: 'site_utility',
            ...buildSiteUtilityTableFilter(pageState, utilities, isFiltersSidebarEnabled),
        },
        {
            key: 'program_id',
            ...buildProgramTableFilter(pageState, programsToDisplay, { includeWithoutProgram: true }, isFiltersSidebarEnabled),
        },
        {
            key: 'lmp_market',
            title: 'Market, Load Zone',
            dataIndex: 'lmp_market',
            sorter: (a: ISite, b: ISite) => compareWithLocale(a.lmp_market, b.lmp_market),
            ...getColumnSortOrder('lmp_market', pageState.sorter),
            render: (text, record) => <SiteMarketAndLoadZone site={record} />,
        },
        {
            key: 'sans',
            title: 'Enrollment ID',
            dataIndex: 'sans',
            render: (text, site) => (
                <SiteSANs market={site.lmp_market} utility={site.site_utility} sans={site.sans} searchString={pageState?.search} />
            ),
        },
        ...(isAdminRole
            ? [
                  {
                      key: 'telemetry_resource_status',
                      title: 'Resource Status',
                      align: 'center',
                      width: 120,
                      hidden: true, // Hide by default
                      render: (text: string, record: ISite) => <ResourceTelemetryStatusCell site={record} />,
                  },
              ]
            : []),

        ...(isAdminRole ? [...buildSiteStatusTableFilter(pageState, isFiltersSidebarEnabled)] : []),
        ...buildSiteDispatchTypeTableFilter(pageState, isFiltersSidebarEnabled),
        ...(isAdminRole || isControlProviderRole
            ? [
                  {
                      key: 'interval_data',
                      title: 'Data',
                      width: 100,
                      dataIndex: 'interval_data',
                      render: (text: string, record: ISite) => {
                          const activeSan = getActiveSiteSan(record);
                          return <IntervalDataTag sanInfo={activeSan?.san_info} />;
                      },
                  },
              ]
            : []),
        ...(isAdminRole
            ? [
                  {
                      key: 'exist_salesforce_meters',
                      title: 'Meters',
                      width: 90,
                      dataIndex: 'exist_salesforce_meters',
                      render: (text: string, record: ISite) => record?.meters?.length,
                  },
              ]
            : []),
        ...(isAdminRole
            ? [
                  {
                      key: 'exist_salesforce_meter_enrollments',
                      title: 'Meter Enrollments',
                      width: 120,
                      dataIndex: 'exist_salesforce_meter_enrollments',
                      render: (text: string, record: ISite) =>
                          record?.meters?.reduce((sum, meter) => (meter?.meter_enrollments?.length ? sum + meter.meter_enrollments.length : sum), 0),
                  },
              ]
            : []),
        ...(isAdminRole
            ? [
                  {
                      key: 'ven_status',
                      title: 'VEN',
                      align: 'center',
                      ellipsis: true,
                      width: 140,
                      hidden: true, // Hide by default
                      render: (text: string, record: ISite) => <SiteVensInfo site={record} />,
                  },
              ]
            : []),
        {
            key: 'action',
            sorter: false,
            render: (text: string, record: ISite) => <ActionList actions={actions} item={record} />,
        },
    ] as CustomizableColumnType<ISite>[];

    const onSelectRow = (selectedRows: Key[]) => {
        handleSelectedItems(selectedRows as number[], sites);
    };

    const pageActions = useMemo(() => {
        return [
            selectedSiteIds.length > 0 && (
                <React.Fragment key="selection-site-actions">
                    {ability.can('attach', 'ControlProvider') && (
                        <Button
                            key="update-fields-site-batch"
                            size="large"
                            onClick={() => setSiteAction(SPECIFIC_SITE_ACTION.UPDATE_SPECIFIC_FIELDS)}
                        >
                            Update ({selectedSiteIds.length})
                        </Button>
                    )}

                    {(ability.can('enroll', 'Program') || ability.can('enroll', 'PriceResponse') || ability.can('enroll', 'CleanResponse')) && (
                        <Dropdown.Button
                            key="enroll-sites-to-program-batch"
                            size="large"
                            overlay={
                                <Menu>
                                    <MenuItem key="unenroll-all-program" onClick={() => setSiteAction(SPECIFIC_SITE_ACTION.BATCH_UNENROLL_PROGRAMS)}>
                                        Remove All Programs from Selection
                                    </MenuItem>
                                </Menu>
                            }
                            onClick={() => setSiteAction(SPECIFIC_SITE_ACTION.ATTACH_PROGRAMS)}
                        >
                            Attach programs ({selectedSiteIds.length})
                        </Dropdown.Button>
                    )}
                    {isAllowedToMerge && (
                        <Button size="large" key="merge-sites" onClick={() => setSiteAction(SPECIFIC_SITE_ACTION.MERGE)}>
                            Merge ({selectedSiteIds.length})
                        </Button>
                    )}
                    <Button size="large" key="clear-selection-action" onClick={() => handleSelectedItems([], sites)}>
                        Clear Selected ({selectedSiteIds.length})
                    </Button>
                </React.Fragment>
            ),
            <SearchInput key="site-search" onSearch={handleSearch} defaultValue={pageState?.search} />,
            ability.can('update', 'Site') && (
                <Dropdown.Button
                    key="actions"
                    size="large"
                    onClick={showImportModal}
                    menu={{
                        items: [
                            {
                                key: 'import-rap',
                                label: (
                                    <WithControlledTooltip extraVisibleCondition={!companyId} title="Please, select company first!" placement="right">
                                        Import Sites RAP data
                                    </WithControlledTooltip>
                                ),
                                icon: <UploadOutlined />,
                                disabled: !companyId,
                                onClick: () => setSiteAction(SPECIFIC_SITE_ACTION.IMPORT_RAPS),
                            },
                        ],
                    }}
                >
                    <UploadOutlined />
                    &nbsp;Import Sites
                </Dropdown.Button>
            ),
            <SiteEnrollmentExportButton filter={toUsefulParams({ ...pageState, companyId: companyId })} key="download-csv" />,
            // Hide the open/close sites button if the sidebar is enabled
            !isFiltersSidebarEnabled && ability.can('close-open', 'Site') && (
                <Button key="open-close-sites" size="large" onClick={toggleClosedSites} disabled={isLoading}>
                    {isLoading ? 'Loading...' : isClosed ? 'Show Open Sites' : 'Show Closed Sites'}
                </Button>
            ),
            ability.can('create', 'AsyncJob') && <SyncSalesforceButton jobName={JobName.SYNC_SALESFORCE} key="sync-salesforce" />,
            ability.can('update', 'Site') && (
                <Button key="new-site" type="primary" onClick={showNewSiteModal} icon={<PlusCircleOutlined />} size="large" data-cy="create-site">
                    New Site
                </Button>
            ),
        ];
    }, [
        ability,
        companyId,
        handleSearch,
        handleSelectedItems,
        isAllowedToMerge,
        isClosed,
        isFiltersSidebarEnabled,
        isLoading,
        pageState,
        selectedSiteIds.length,
        sites,
        toggleClosedSites,
    ]);

    return (
        <>
            <CustomizableTableContextProvider configKey="sites" columns={siteColumns}>
                <Layout style={{ height: '100%', paddingLeft: layoutPaddingLeft }}>
                    {isFiltersSidebarEnabled && (
                        <SitesSidebar
                            collapsed={sidebarCollapsed}
                            onCollapseChange={(collapsed: boolean) => setSidebarCollapsed(collapsed)}
                            totalRecords={meta?.total}
                            onFilterChange={onSidebarFilterChange}
                            gridComponent={<SidebarGrid />}
                        />
                    )}

                    <Layout.Content style={{ padding: '0 26px' }}>
                        <PageHeader
                            pageTitle={`Sites ${!isFiltersSidebarEnabled ? `(${meta?.total})` : ''}`}
                            actions={pageActions}
                            extra={[
                                <ViewSwitcher
                                    viewOptions={siteViewInterface(ability)}
                                    key="program-view-switcher"
                                    view={location.pathname}
                                    handleViewMode={onViewChange}
                                />,
                            ]}
                        />

                        <CustomizableTable<ISite>
                            size="small"
                            rowKey="site_id"
                            className="sites-table"
                            showHiddenColumnsCount
                            sticky={{ offsetHeader: 62 }}
                            rowClassName={record => (record.site_clean_response_enrolled ? 'enrolled-site-row' : '')}
                            dataSource={sites}
                            loading={isFetching}
                            onChange={onTableChange}
                            rowSelection={
                                ability.can('update', 'Site')
                                    ? {
                                          selectedRowKeys: selectedSiteIds,
                                          onChange: onSelectRow,
                                          preserveSelectedRowKeys: true,
                                          getCheckboxProps: (record: Record<string, any>) => {
                                              const site = record as ISite;
                                              return {
                                                  // Disable all updates for closed sites
                                                  disabled: !!site.closed_at,
                                              };
                                          },
                                      }
                                    : undefined
                            }
                            meta={meta}
                            expandable={{
                                expandedRowRender: site => <ExtraSiteInformation site={site} />,
                                rowExpandable: site => isAdminRole! && (!!site.meters.length || !!site.sans.length),
                            }}
                        />

                        <PageFooter />
                    </Layout.Content>
                </Layout>
            </CustomizableTableContextProvider>

            <>
                {siteAction === SPECIFIC_SITE_ACTION.UPDATE_SPECIFIC_FIELDS && (
                    <UpdateSitesBatchModal
                        companies={companies}
                        sitesId={selectedSiteIds}
                        onCancel={closeSiteBatchModal}
                        selectedSites={selectedSites}
                    />
                )}

                {site && siteAction === PAGE_ACTION.NEW_EDIT && (
                    <SiteFormModal site={site} companies={companies} programs={programs} onClose={onSiteFormModalClose} />
                )}

                {site && siteAction === SPECIFIC_SITE_ACTION.CLOSE_SITE && <CloseSiteModal site={site as ISite} onCancel={onSiteFormModalClose} />}

                {site && siteAction === SPECIFIC_SITE_ACTION.OPEN_SITE && <OpenSiteModal site={site as ISite} onCancel={onSiteFormModalClose} />}

                {site && siteAction === PAGE_ACTION.AUDIT_TRAIL && (
                    <AuditTrailModal onClose={onSiteFormModalClose} entityId={site.site_id!} entityType={AuditEntityType.SITE} />
                )}

                {siteAction === SPECIFIC_SITE_ACTION.IMPORT && <ImportSitesModal onCancel={onImportModalClose} />}

                {site && siteAction === SPECIFIC_SITE_ACTION.ADD_VEN && (
                    <CustomerVenFormModal
                        ven={prepareVen(site as ISite)}
                        onClose={onSiteFormModalClose}
                        isEditMode={Boolean(prepareVen(site as ISite).ven_id)}
                    />
                )}

                {site && siteAction === PAGE_ACTION.DELETE && (
                    <DeleteSiteModal site={site as ISite} onSubmit={deleteSiteAction} isLoading={isDeleteLoading} onClose={onSiteFormModalClose} />
                )}

                {siteAction === SPECIFIC_SITE_ACTION.MERGE && <MergeSitesModal selectedSites={selectedSites} onClose={onMergeSitesModalClose} />}

                {site && siteAction === PAGE_ACTION.ENROLL_PROGRAM && (
                    <EnrollProgramModal
                        site={site as ISite}
                        programs={programs}
                        onClose={onEnrollProgramModalClose}
                        loading={isEnrollProgramLoading}
                    />
                )}

                {siteAction === SPECIFIC_SITE_ACTION.BATCH_UNENROLL_PROGRAMS && (
                    <BatchUnenrollProgramsModal onCancel={closeBatchUnenrollModal} sites={selectedSites} />
                )}

                {siteAction === SPECIFIC_SITE_ACTION.ATTACH_PROGRAMS && (
                    <AttachProgramBatchModal sitesId={selectedSiteIds} onCancel={closeSiteBatchModal} selectedSites={selectedSites} />
                )}

                {siteAction === SPECIFIC_SITE_ACTION.IMPORT_RAPS && companyId && <SiteRapsImportModal companyId={companyId} onClose={closeModal} />}

                {siteAction === SPECIFIC_SITE_ACTION.VIEW_RAP && <ViewRapInfoModal site={site as ISite} onClose={closeModal} />}
            </>
        </>
    );
}
